import { useMemo } from 'react';
import { checkAccess } from '@/creditflowresearch/application/User/access/checkAccess';
import { type IAccessCondition } from '@/creditflowresearch/ui/common/hooks/access/useAccessConditions';

interface IUseLockDetectionOptions {
  activeItems?: Array<{ hasAccess: boolean }>,
  accessConditionsList?: IAccessCondition[],
  hasAccessConditions?: boolean,
  blockingAccessConditions?: IAccessCondition[],
}

interface IUseLockDetectionResult {
  shouldShowLock: boolean,
  hasBlockingAccessFailure: boolean,
}

export const checkAccessConditionsFailure = (accessConditions?: IAccessCondition[]): boolean => {
  if (!accessConditions || accessConditions.length === 0) {
    return false;
  }

  return accessConditions.some((condition) => !checkAccess(condition.checks));
};

export const useLockDetection = ({
  activeItems = [],
  accessConditionsList = [],
  hasAccessConditions = false,
  blockingAccessConditions,
}: IUseLockDetectionOptions): IUseLockDetectionResult => {
  const hasLockedItems = useMemo(() => {
    return activeItems.some((item) => item.hasAccess === false);
  }, [activeItems]);

  const hasFailedAccessCheck = useMemo(() => {
    if (hasAccessConditions && accessConditionsList.length > 0) {
      return checkAccessConditionsFailure(accessConditionsList);
    }
    return false;
  }, [hasAccessConditions, accessConditionsList]);

  const hasBlockingAccessFailure = useMemo(() => {
    return checkAccessConditionsFailure(blockingAccessConditions);
  }, [blockingAccessConditions]);

  const shouldShowLock = hasLockedItems || hasFailedAccessCheck || hasBlockingAccessFailure;

  return {
    shouldShowLock,
    hasBlockingAccessFailure,
  };
};
